import React from 'react';
import { Box, Button, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, Typography } from '@mui/material';
import { Bot, BotDocument } from '../lib/types';
import { DOC_SORT_KEYS, DocSortKey, SCAN_LABEL, filterDocs, fmtBytes, isDownloadable, sortDocs } from '../lib/botPage';
import { fmtIct } from '../lib/ict';
import { FONT, WARN_TEXT, TABLE_HEAD_SX } from '../lib/brand';

const base = (slug: string) => `/api/admin/bot-ops/bots/${encodeURIComponent(slug)}/documents`;
const COLS: { key: DocSortKey; label: string }[] = [
  { key: 'title', label: 'Title' }, { key: 'type', label: 'Type' }, { key: 'date', label: 'Date' }, { key: 'modified', label: 'Modified' },
  { key: 'size', label: 'Size' }, { key: 'version', label: 'Version' }, { key: 'scan', label: 'Scan' },
];

/** Every file the bot created. Withheld files (secret, personal data, too large, unreadable) show why and have no link. */
export function DocumentsTab({ bot, docs }: { bot: Bot; docs: BotDocument[] }) {
  const [f, setF] = React.useState({ q: '', type: 'all', scan: 'all' });
  const [sort, setSort] = React.useState<{ key: DocSortKey; dir: 'asc' | 'desc' }>({ key: 'date', dir: 'desc' });
  const types = React.useMemo(() => Array.from(new Set(docs.map((d) => d.type))).sort(), [docs]);
  const shown = React.useMemo(() => sortDocs(filterDocs(docs, f), sort.key, sort.dir), [docs, f, sort]);
  const downloadable = docs.filter(isDownloadable).length;

  if (docs.length === 0) {
    return <Typography variant="body2" color="text.secondary" data-testid="no-docs">No documents found (checked recommendations/{bot.slug}/ and status/{bot.slug}.json).</Typography>;
  }
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.2 }}>
        <TextField size="small" label="Search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} sx={{ minWidth: 220 }} />
        <TextField select size="small" label="Type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} sx={{ minWidth: 170 }}>
          <MenuItem value="all">All types</MenuItem>
          {types.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Scan" value={f.scan} onChange={(e) => setF({ ...f, scan: e.target.value })} sx={{ minWidth: 170 }}>
          <MenuItem value="all">All files</MenuItem>
          <MenuItem value="clean">Clean</MenuItem>
          <MenuItem value="binary_unscanned">Not scanned (binary)</MenuItem>
          <MenuItem value="withheld">Withheld</MenuItem>
        </TextField>
        <Box sx={{ flex: 1 }} />
        {downloadable > 0
          ? <Button component="a" href={`${base(bot.slug)}.zip`} download size="small" variant="contained" data-testid="download-all">Download all ({downloadable})</Button>
          : <Button size="small" variant="contained" disabled>Download all</Button>}
      </Box>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.8 }}>{shown.length} of {docs.length} files · {docs.length - downloadable} withheld or not uploaded</Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label="Documents" sx={{ minWidth: 820 }}>
          <TableHead sx={TABLE_HEAD_SX}>
            <TableRow>
              {COLS.map((c) => (
                <TableCell key={c.key} aria-sort={sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
                  {DOC_SORT_KEYS.includes(c.key) && (
                    <TableSortLabel active={sort.key === c.key} direction={sort.key === c.key ? sort.dir : 'asc'} onClick={() => setSort((s) => (s.key === c.key ? { key: c.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: c.key, dir: c.key === 'date' || c.key === 'modified' ? 'desc' : 'asc' }))}>{c.label}</TableSortLabel>
                  )}
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((d) => {
              const ok = isDownloadable(d);
              return (
                <TableRow key={d.sha256 + d.path} data-testid="doc-row">
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{d.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: FONT.mono, wordBreak: 'break-all' }} component="div">{d.path}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{d.type}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{d.created_at || 'no data'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtIct(d.modified_at)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtBytes(d.size_bytes)}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{d.version || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: ok ? 'text.primary' : WARN_TEXT, fontWeight: ok ? 400 : 700 }}>{SCAN_LABEL[d.scan.status] || d.scan.status}</TableCell>
                  <TableCell>
                    {ok
                      ? <Button component="a" size="small" href={`${base(bot.slug)}/${d.sha256}/download`} download={d.filename} aria-label={`Download ${d.filename}`} sx={{ textTransform: 'none' }}>Download</Button>
                      : <Typography variant="caption" color="text.secondary">Withheld</Typography>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
